// File: components/Canvas.tsx
import { Box, Button, Divider } from "@mui/material";
import ComponentRenderer from "./ComponentRenderer";
import { useMemo, useEffect } from "react";
import AddIcon from "@mui/icons-material/Add";
import "./PageBuilding.module.css";

const Canvas = ({
  components,
  selectedId,
  onSelect,
  onUpdate,
  header,
  footer,
  pageUrlComponents,
  headerMenu,
  onEditWidget,
  theme,
  pageDictionary,
  setShowSidebar,
  setIsTextEditing,
  isTextEditing,
  setUnsavedText,
}: any) => {
  // In Canvas.tsx
  const rootComponents = useMemo(
    () =>
      components
        .filter((c: any) => c.parentId === null)
        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0)),
    [components]
  );

  useEffect(() => {
    if (!selectedId) return;

    const el = document.getElementById(`component-${selectedId}`);
    if (!el) return;

    // Smoothly scroll the element into the Canvas container
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    // try to focus (Paper/Text blocks will be made focusable with tabIndex=-1)
    try {
      (el as HTMLElement).focus?.();
    } catch (e) {
      // ignore
    }
  }, [selectedId]);

  // Add custom style to remove body border
  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.innerHTML = `
    body::before {
      border: none !important;
    }
  `;
    document.head.appendChild(styleEl);

    return () => {
      document.head.removeChild(styleEl); // cleanup
    };
  }, []);

  return (
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        backgroundColor: theme === "black" ? "#000000" : "#ffffff",
        m: 1,
        p: 0.5,
      }}
    >
      <div id="wrpr" className="htmlWrapper">
        {pageUrlComponents as any}
        {header as any}
        {headerMenu as any}
        <Box>
          <Divider sx={{ margin: "8px 0" }} />
          <Box>
            {components
              .filter((c: any) => c.parentId === null)
              .filter((c: any) => {
                // Keep all non-widget components
                if (c.type !== "widgets") return true;
                // Only keep widgets with internal-widgets class
                return c.props.className?.includes("internal-widget");
              })
              .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
              .map((component: any) => (
                <ComponentRenderer
                  key={component.id}
                  component={component}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  onUpdate={onUpdate}
                  onEditWidget={onEditWidget}
                  theme={theme}
                  pageDictionary={pageDictionary}
                  setIsTextEditing={setIsTextEditing}
                  isTextEditing={isTextEditing}
                  setUnsavedText={setUnsavedText}
                />
              ))}

            {rootComponents.length === 0 && (
              <Box textAlign="center" color="text.secondary" mt={3}>
                <Button
                  variant="contained"
                  onClick={() => setShowSidebar(true)}
                  endIcon={<AddIcon />}
                  sx={{
                    color: "text.primary",
                    borderRadius: "12px",
                    borderColor: "#0000003B",
                    boxShadow: `
                          0px 2px 1px -1px #00000033,
                          0px 1px 1px 0px #00000024,
                          0px 1px 3px 0px #0000001F
                      `,
                    background: "#ffffff !Important",
                  }}
                >
                  ADD CONTENT
                </Button>
              </Box>
            )}
          </Box>
          {footer as any}
        </Box>
      </div>
    </Box>
  );
};
export default Canvas;
